import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Field,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  SegmentGroup,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LuArrowLeft, LuCalculator, LuReceipt } from 'react-icons/lu';
import { amortize } from 'shared/amortization';
import type { AmortizationResult } from 'shared/amortization';
import {
  createContract,
  getProjectSettings,
  listClients,
  listSellers,
  uploadContractDocument,
  uploadReceipt,
  type AmortizationMethod,
  type ClientListRow,
  type PaymentMethod,
  type SaleType,
  type SellerOption,
} from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toaster } from '@/components/ui/toaster';
import { ScheduleTable } from '@/components/contract/ScheduleTable';
import { fmtMoney, percentToFraction, toMoneyString } from '@/utils/format';
import { RequiredLabel, RequiredLegend } from '@/components/ui/required-field';

type FormValues = {
  saleType: SaleType;
  buyerId: string;
  sellerId: string;
  financedAmount: string;
  downPayment: string;
  ratePercent: string;
  termMonths: string;
  startDate: string;
  method: AmortizationMethod;
  adminFee: string;
  moraPercent: string;
  graceDays: string;
  interestStartMonth: string;
  lotNumber: string;
  lotManzana: string;
  lotAreaM2: string;
  lotListPrice: string;
  lotAddress: string;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  paymentReference: string;
};

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY: FormValues = {
  saleType: 'financed',
  buyerId: '',
  sellerId: '',
  financedAmount: '',
  downPayment: '0',
  ratePercent: '',
  termMonths: '',
  startDate: TODAY,
  method: 'french',
  adminFee: '0',
  moraPercent: '0',
  graceDays: '0',
  interestStartMonth: '1',
  lotNumber: '',
  lotManzana: '',
  lotAreaM2: '',
  lotListPrice: '',
  lotAddress: '',
  paymentMethod: 'cash',
  paymentDate: TODAY,
  paymentReference: '',
};

const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'deposit', label: 'Depósito' },
  { value: 'check', label: 'Cheque' },
];

export function ContractNew() {
  const navigate = useNavigate();
  const { activeProject } = useProject();
  const [searchParams] = useSearchParams();
  const buyerIdParam = searchParams.get('buyerId') ?? '';
  const [clients, setClients] = useState<ClientListRow[]>([]);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [values, setValues] = useState<FormValues>({ ...EMPTY, buyerId: buyerIdParam });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [engancheReceiptFile, setEngancheReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCash = values.saleType === 'cash';

  useEffect(() => {
    if (!activeProject) return;
    listClients({ projectId: activeProject.id, status: 'active' })
      .then(setClients)
      .catch(() => setClients([]));
    listSellers({ status: 'active' })
      .then((rows) => setSellers(rows.map((s) => ({ id: s.id, fullName: s.fullName }))))
      .catch(() => setSellers([]));
    getProjectSettings(activeProject.id)
      .then((s) =>
        setValues((v) => ({
          ...v,
          adminFee: s.defaultAdminFee,
          moraPercent: (Number(s.defaultMoraMonthlyRate) * 100).toString(),
          graceDays: String(s.defaultMoraGraceDays),
        })),
      )
      .catch(() => {});
  }, [activeProject?.id]);

  // Autofill the lot address from the project's default address (editable per contract).
  // Only fills when the field is still blank so it never clobbers what the user typed.
  useEffect(() => {
    const projectAddress = activeProject?.address?.trim();
    if (!projectAddress) return;
    setValues((v) => (v.lotAddress.trim() ? v : { ...v, lotAddress: projectAddress }));
  }, [activeProject?.id, activeProject?.address]);

  // Keep the interest-start month within the current term. If the term drops
  // below the selected start (or becomes < 2 months), fall back to Mes 1.
  useEffect(() => {
    setValues((v) => {
      const termN = Number(v.termMonths);
      const maxStart = Number.isInteger(termN) ? termN - 1 : 0;
      const cur = Number(v.interestStartMonth);
      if (maxStart < 1 || cur > maxStart) {
        return v.interestStartMonth === '1' ? v : { ...v, interestStartMonth: '1' };
      }
      return v;
    });
  }, [values.termMonths]);

  const chargesApply = !isCash && Number(percentToFraction(values.ratePercent) || '0') > 0;
  // Hoisted so the "aplicar intereses desde" dropdown gate and its month-list
  // length share one parse of values.termMonths instead of computing it twice.
  const termN = Number(values.termMonths);

  const preview: AmortizationResult | null = useMemo(() => {
    if (isCash) return null;
    const principal = toMoneyString(values.financedAmount);
    const annualRate = percentToFraction(values.ratePercent);
    const termMonths = Number(values.termMonths);
    if (!principal || Number(principal) <= 0) return null;
    if (annualRate === '' || Number(annualRate) < 0) return null;
    if (!Number.isInteger(termMonths) || termMonths <= 0 || termMonths > 600) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(values.startDate)) return null;
    try {
      return amortize({
        principal,
        annualRate,
        termMonths,
        interestStartMonth: chargesApply ? Number(values.interestStartMonth) : 1,
        startDate: values.startDate,
        method: values.method,
        dayCount: '30/360',
        roundingMode: 'half_even',
      });
    } catch {
      return null;
    }
  }, [values, isCash]);

  const cashValid =
    Number(toMoneyString(values.financedAmount) || '0') > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(values.startDate) &&
    /^\d{4}-\d{2}-\d{2}$/.test(values.paymentDate) &&
    !!values.paymentMethod;

  function switchMode(next: SaleType) {
    setValues((v) => {
      // Default the cash price from the lot list price when first switching, if blank.
      const financedAmount =
        next === 'cash' && !v.financedAmount.trim() && v.lotListPrice.trim()
          ? v.lotListPrice
          : v.financedAmount;
      return { ...v, saleType: next, financedAmount };
    });
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!activeProject) return;
    if (!values.buyerId) {
      setError('Selecciona un cliente.');
      return;
    }
    if (!values.lotNumber.trim() || !values.lotManzana.trim()) {
      setError('Ingresa el número y la manzana del terreno.');
      return;
    }
    if (isCash ? !cashValid : !preview) {
      setError(
        isCash
          ? 'Revisa el precio de venta y los datos del pago.'
          : 'Revisa los parámetros: monto, tasa, plazo y fecha de inicio.',
      );
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const lot = {
        number: values.lotNumber.trim(),
        manzana: values.lotManzana.trim(),
        ...(values.lotAreaM2 ? { areaM2: toMoneyString(values.lotAreaM2) || '0.00' } : {}),
        ...(values.lotListPrice ? { listPrice: toMoneyString(values.lotListPrice) || '0.00' } : {}),
        address: values.lotAddress.trim() || undefined,
      };
      const result = isCash
        ? await createContract({
            saleType: 'cash',
            projectId: activeProject.id,
            buyerId: values.buyerId,
            sellerId: values.sellerId || null,
            financedAmount: toMoneyString(values.financedAmount),
            startDate: values.startDate,
            lot,
            payment: {
              method: values.paymentMethod,
              paymentDate: values.paymentDate,
              ...(values.paymentReference.trim() ? { reference: values.paymentReference.trim() } : {}),
            },
          })
        : await createContract({
            saleType: 'financed',
            projectId: activeProject.id,
            buyerId: values.buyerId,
            sellerId: values.sellerId || null,
            financedAmount: toMoneyString(values.financedAmount),
            downPayment: toMoneyString(values.downPayment) || '0.00',
            interestRateAnnual: percentToFraction(values.ratePercent),
            termMonths: Number(values.termMonths),
            method: values.method,
            dayCount: '30/360',
            startDate: values.startDate,
            ...(chargesApply
              ? {
                  adminFee: toMoneyString(values.adminFee) || '0.00',
                  moraMonthlyRate: (Number(values.moraPercent) / 100).toFixed(6),
                  moraGraceDays: Math.max(0, Math.trunc(Number(values.graceDays) || 0)),
                  interestStartMonth: Number(values.interestStartMonth),
                }
              : {}),
            lot,
          });

      if (isCash && receiptFile && result.paymentId) {
        try {
          await uploadReceipt(result.paymentId, receiptFile);
        } catch {
          toaster.create({
            title: 'Contrato creado, pero no se pudo adjuntar el comprobante',
            description: 'Puedes subirlo luego desde la pestaña Pagos.',
            type: 'warning',
          });
        }
      }

      if (!isCash && engancheReceiptFile && Number(values.downPayment) > 0 && result.id) {
        try {
          await uploadContractDocument(result.id, engancheReceiptFile, 'voucher', 'Comprobante de enganche');
        } catch {
          toaster.create({
            title: 'Contrato creado, pero no se pudo adjuntar el comprobante de enganche',
            description: 'Puedes subirlo luego desde la pestaña Documentos.',
            type: 'warning',
          });
        }
      }

      toaster.create({ title: 'Contrato creado', description: result.contractNumber, type: 'success' });
      navigate(`/contratos/${result.id}`);
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      const msg =
        raw === 'lot_already_sold'
          ? 'Este terreno ya está vendido. Elige otro número/manzana o cancela el contrato que lo retiene.'
          : raw || 'No se pudo crear el contrato';
      setError(msg);
      toaster.create({ title: 'No se pudo crear el contrato', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  if (!activeProject) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="10" textAlign="center" maxW="2xl" mx="auto">
          <Text color="fg.muted">Selecciona un proyecto activo antes de crear un contrato.</Text>
        </Box>
      </Box>
    );
  }

  const submitDisabled =
    (isCash ? !cashValid : !preview) || !values.buyerId || !values.lotNumber.trim() || !values.lotManzana.trim();

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack gap="1">
          {buyerIdParam && (
            <Button
              variant="ghost"
              size="sm"
              alignSelf="flex-start"
              onClick={() => navigate(`/clientes/${buyerIdParam}`)}
            >
              <Icon as={LuArrowLeft} /> Volver al cliente
            </Button>
          )}
          <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
            {activeProject.code} · Contratos
          </Text>
          <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
            Nuevo contrato
          </Heading>
          <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
            El número de contrato <strong>{activeProject.code}-C-NNN</strong> se asigna automáticamente.
          </Text>
        </Stack>

        <Stack direction={{ base: 'column', lg: 'row' }} gap="6" align="flex-start">
          <Box flex="1" bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }} w="full">
            <Heading size="md" mb="5">
              Parámetros
            </Heading>
            <form onSubmit={handleSubmit}>
              <Stack gap="4">
                <Field.Root required>
                  <RequiredLabel>Cliente</RequiredLabel>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={values.buyerId}
                      onChange={(e) => setValues({ ...values, buyerId: e.currentTarget.value })}
                    >
                      <option value="">Selecciona un cliente…</option>
                      {clients.map((cl) => (
                        <option key={cl.id} value={cl.id}>
                          {cl.code} · {cl.fullName}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label>Tipo de venta</Field.Label>
                  <SegmentGroup.Root
                    value={values.saleType}
                    onValueChange={(e) => e.value && switchMode(e.value as SaleType)}
                    // Hug the options instead of stretching to full width.
                    width="fit-content"
                    // The recipe's default --segment-indicator-bg is a _light/_dark
                    // conditional that doesn't resolve under our base/_dark theme, so the
                    // selected card had no background. Set it from an unconditional token.
                    css={{ '--segment-indicator-bg': 'colors.bg.surface' }}
                  >
                    <SegmentGroup.Indicator />
                    {[
                      { value: 'financed', label: 'Financiado' },
                      { value: 'cash', label: 'Pago de contado' },
                    ].map((o) => (
                      <SegmentGroup.Item key={o.value} value={o.value}>
                        <SegmentGroup.ItemText>{o.label}</SegmentGroup.ItemText>
                        <SegmentGroup.ItemHiddenInput />
                      </SegmentGroup.Item>
                    ))}
                  </SegmentGroup.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label>Vendedor (opcional)</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={values.sellerId}
                      onChange={(e) => setValues({ ...values, sellerId: e.currentTarget.value })}
                    >
                      <option value="">Sin vendedor</option>
                      {sellers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.fullName}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                  <Field.Root flex="1" required>
                    <RequiredLabel>{isCash ? 'Precio de venta (Q)' : 'Monto financiado (Q)'}</RequiredLabel>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={values.financedAmount}
                      onChange={(e) => setValues({ ...values, financedAmount: e.currentTarget.value })}
                      placeholder="100000.00"
                    />
                  </Field.Root>
                  {!isCash && (
                    <Field.Root flex="1">
                      <Field.Label>Enganche (Q)</Field.Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={values.downPayment}
                        onChange={(e) => setValues({ ...values, downPayment: e.currentTarget.value })}
                        placeholder="0.00"
                      />
                    </Field.Root>
                  )}
                </Stack>

                {!isCash && Number(values.downPayment) > 0 && (
                  <Field.Root>
                    <Field.Label>Comprobante de enganche (opcional)</Field.Label>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      onChange={(e) => setEngancheReceiptFile(e.currentTarget.files?.[0] ?? null)}
                    />
                  </Field.Root>
                )}

                {!isCash && (
                  <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                    <Field.Root flex="1" required>
                      <RequiredLabel>Tasa anual (%)</RequiredLabel>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={values.ratePercent}
                        onChange={(e) => setValues({ ...values, ratePercent: e.currentTarget.value })}
                        placeholder="18"
                      />
                    </Field.Root>
                    <Field.Root flex="1" required>
                      <RequiredLabel>Plazo (meses)</RequiredLabel>
                      <Input
                        type="number"
                        min="1"
                        max="600"
                        step="1"
                        value={values.termMonths}
                        onChange={(e) => setValues({ ...values, termMonths: e.currentTarget.value })}
                        placeholder="60"
                      />
                    </Field.Root>
                  </Stack>
                )}

                <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                  <Field.Root flex="1" required>
                    <RequiredLabel>{isCash ? 'Fecha de venta' : 'Fecha de inicio'}</RequiredLabel>
                    <Input
                      type="date"
                      value={values.startDate}
                      onChange={(e) => setValues({ ...values, startDate: e.currentTarget.value })}
                    />
                  </Field.Root>
                  {!isCash && (
                    <Field.Root flex="1">
                      <Field.Label>Método</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={values.method}
                          onChange={(e) =>
                            setValues({ ...values, method: e.currentTarget.value as AmortizationMethod })
                          }
                        >
                          <option value="french">Francés (cuota fija)</option>
                          <option value="german">Alemán (capital fijo)</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  )}
                </Stack>

                {chargesApply && termN >= 2 && (
                  <Field.Root>
                    <Field.Label>Aplicar intereses desde</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={values.interestStartMonth}
                        onChange={(e) =>
                          setValues({ ...values, interestStartMonth: e.currentTarget.value })
                        }
                      >
                        {Array.from({ length: termN - 1 }, (_, i) => i + 1).map((mo) => (
                          <option key={mo} value={String(mo)}>
                            Mes {mo}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Field.HelperText>
                      Los intereses se acumulan desde esta cuota. Antes, el pago abona solo a capital.
                    </Field.HelperText>
                  </Field.Root>
                )}

                {!isCash && (
                  <Field.Root>
                    <Field.Label>Base de cálculo</Field.Label>
                    <Input value="30/360" disabled />
                    <Field.HelperText>Los intereses se calculan con base 30/360.</Field.HelperText>
                  </Field.Root>
                )}

                <Box borderTopWidth="1px" borderColor="border" pt="4">
                  <Heading size="sm" mb="3">
                    Terreno
                  </Heading>
                  <Stack gap="4">
                    <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                      <Field.Root flex="1" required>
                        <RequiredLabel>Número de terreno</RequiredLabel>
                        <Input
                          value={values.lotNumber}
                          onChange={(e) => setValues({ ...values, lotNumber: e.currentTarget.value })}
                          placeholder="15"
                        />
                      </Field.Root>
                      <Field.Root flex="1" required>
                        <RequiredLabel>Manzana</RequiredLabel>
                        <Input
                          value={values.lotManzana}
                          onChange={(e) => setValues({ ...values, lotManzana: e.currentTarget.value })}
                          placeholder="B"
                        />
                      </Field.Root>
                    </Stack>
                    <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                      <Field.Root flex="1">
                        <Field.Label>Área (m²)</Field.Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={values.lotAreaM2}
                          onChange={(e) => setValues({ ...values, lotAreaM2: e.currentTarget.value })}
                          placeholder="250.00"
                        />
                      </Field.Root>
                      <Field.Root flex="1">
                        <Field.Label>Precio de lista (Q)</Field.Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={values.lotListPrice}
                          onChange={(e) => setValues({ ...values, lotListPrice: e.currentTarget.value })}
                          placeholder="120000.00"
                        />
                      </Field.Root>
                    </Stack>
                    <Field.Root>
                      <Field.Label>Dirección</Field.Label>
                      <Input
                        value={values.lotAddress}
                        onChange={(e) => setValues({ ...values, lotAddress: e.currentTarget.value })}
                        placeholder="Ubicación del inmueble"
                      />
                    </Field.Root>
                  </Stack>
                </Box>

                {isCash && (
                  <Box borderTopWidth="1px" borderColor="border" pt="4">
                    <Heading size="sm" mb="3">
                      Pago
                    </Heading>
                    <Stack gap="4">
                      <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                        <Field.Root flex="1" required>
                          <RequiredLabel>Forma de pago</RequiredLabel>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              value={values.paymentMethod}
                              onChange={(e) =>
                                setValues({ ...values, paymentMethod: e.currentTarget.value as PaymentMethod })
                              }
                            >
                              {PAYMENT_METHOD_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        <Field.Root flex="1" required>
                          <RequiredLabel>Fecha de pago</RequiredLabel>
                          <Input
                            type="date"
                            value={values.paymentDate}
                            onChange={(e) => setValues({ ...values, paymentDate: e.currentTarget.value })}
                          />
                        </Field.Root>
                      </Stack>
                      <Field.Root>
                        <Field.Label>Referencia (opcional)</Field.Label>
                        <Input
                          value={values.paymentReference}
                          onChange={(e) => setValues({ ...values, paymentReference: e.currentTarget.value })}
                          placeholder="No. de transferencia / recibo"
                        />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>Comprobante (opcional)</Field.Label>
                        <input
                          type="file"
                          accept="application/pdf,image/jpeg,image/png"
                          onChange={(e) => setReceiptFile(e.currentTarget.files?.[0] ?? null)}
                        />
                      </Field.Root>
                    </Stack>
                  </Box>
                )}

                {chargesApply && (
                  <Stack direction={{ base: 'column', md: 'row' }} gap="4">
                    <Field.Root flex="1">
                      <Field.Label>Costo administrativo (Q/mes)</Field.Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={values.adminFee}
                        onChange={(e) => setValues({ ...values, adminFee: e.currentTarget.value })}
                        placeholder="0.00"
                      />
                    </Field.Root>
                    <Field.Root flex="1">
                      <Field.Label>Tasa de mora (%/mes)</Field.Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={values.moraPercent}
                        onChange={(e) => setValues({ ...values, moraPercent: e.currentTarget.value })}
                        placeholder="0"
                      />
                    </Field.Root>
                    <Field.Root flex="1">
                      <Field.Label>Días de gracia</Field.Label>
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        value={values.graceDays}
                        onChange={(e) => setValues({ ...values, graceDays: e.currentTarget.value })}
                        placeholder="0"
                      />
                    </Field.Root>
                  </Stack>
                )}

                {error && (
                  <Text color="red.600" fontSize="sm">
                    {error}
                  </Text>
                )}

                <RequiredLegend />

                <Stack direction={{ base: 'column-reverse', md: 'row' }} gap="3" justify="flex-end">
                  <Button variant="ghost" onClick={() => navigate('/contratos')} disabled={submitting} w={{ base: 'full', md: 'auto' }}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                    loading={submitting}
                    loadingText="Creando…"
                    disabled={submitDisabled}
                    w={{ base: 'full', md: 'auto' }}
                  >
                    Crear contrato
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Box>

          <Box flex="1" w="full">
            <HStack gap="2" mb="3" color="fg.muted">
              <Icon as={isCash ? LuReceipt : LuCalculator} />
              <Text fontWeight="700">{isCash ? 'Resumen de pago al contado' : 'Vista previa del cronograma'}</Text>
            </HStack>
            {isCash ? (
              <CashSummary
                price={toMoneyString(values.financedAmount)}
                paymentDate={values.paymentDate}
                method={values.paymentMethod}
              />
            ) : preview ? (
              <Stack gap="3">
                <SimpleGrid columns={3} gap="3">
                  <PreviewStat label="1ª cuota" value={fmtMoney(preview.rows[0]?.totalAmount ?? '0')} />
                  <PreviewStat label="Total intereses" value={fmtMoney(preview.totalInterest)} />
                  <PreviewStat label="Total a pagar" value={fmtMoney(preview.totalPaid)} />
                </SimpleGrid>
                <ScheduleTable rows={preview.rows} />
              </Stack>
            ) : (
              <Box bg="bg.subtle" rounded="lg" p="8" textAlign="center" color="fg.muted">
                <Text fontSize="sm">Completa monto, tasa, plazo y fecha para ver el cronograma.</Text>
              </Box>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}

function CashSummary({ price, paymentDate, method }: { price: string; paymentDate: string; method: PaymentMethod }) {
  const methodLabel = PAYMENT_METHOD_OPTIONS.find((o) => o.value === method)?.label ?? method;
  return (
    <Stack gap="3">
      <SimpleGrid columns={2} gap="3">
        <PreviewStat label="Precio de venta" value={fmtMoney(price || '0')} />
        <PreviewStat label="Total a pagar" value={fmtMoney(price || '0')} />
      </SimpleGrid>
      <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
        <Stack gap="1.5" fontSize="sm">
          <HStack justify="space-between" gap="3">
            <Text color="fg.muted">Fecha de pago</Text>
            <Text textAlign="end">{paymentDate || '—'}</Text>
          </HStack>
          <HStack justify="space-between" gap="3">
            <Text color="fg.muted">Forma de pago</Text>
            <Text textAlign="end">{methodLabel}</Text>
          </HStack>
        </Stack>
      </Box>
      <Text fontSize="xs" color="fg.muted">
        El pago al contado no genera un cronograma. El derecho de agua (Q 3,000.00) se cobra por separado.
      </Text>
    </Stack>
  );
}

function PreviewStat({ label, value }: { label: string; value: string }) {
  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="3">
      <Text fontSize="2xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
        {label}
      </Text>
      <Text fontSize="lg" fontWeight="800" letterSpacing="-0.01em">
        {value}
      </Text>
    </Box>
  );
}
